export default function UseOfFundsSection() {
  const allocations = [
    {
      percentage: 75,
      label: "Property Acquisition",
      desc: "Direct acquisition of UK assisted living properties through the SPV, including purchase price and associated costs of completing the property transactions.",
      icon: "ri-building-2-line",
      color: "#C9A84C",
    },
    {
      percentage: 15,
      label: "Transaction Costs",
      desc: "Legal fees, stamp duty land tax (SDLT), surveying fees, title insurance, and all professional costs associated with completing property acquisitions.",
      icon: "ri-file-text-line",
      color: "#4CAF8A",
    },
    {
      percentage: 10,
      label: "Asset Setup & Working Capital",
      desc: "Establishment of the income-generating asset structure, SPV formation costs, ongoing operational reserves, and working capital held for the investment period.",
      icon: "ri-settings-3-line",
      color: "#6B8F6B",
    },
  ];

  return (
    <section className="bg-[#EEF2F9] py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-widest block mb-3">
            Capital Deployment
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#071A2E]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Use of Funds
          </h2>
          <p className="text-slate-500 text-sm mt-3 max-w-lg mx-auto">
            Investor capital is deployed through a transparent, structured process. The breakdown below illustrates how each pound invested is allocated.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Visual Breakdown */}
          <div className="flex flex-col gap-5">
            {allocations.map((a) => (
              <div key={a.label} className="bg-white rounded-2xl p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ backgroundColor: `${a.color}18` }}
                  >
                    <i className={`${a.icon} text-lg leading-none`} style={{ color: a.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-[#071A2E] text-sm">{a.label}</span>
                      <span
                        className="text-2xl font-bold"
                        style={{ color: a.color, fontFamily: "'Playfair Display', serif" }}
                      >
                        {a.percentage}%
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{ width: `${a.percentage}%`, backgroundColor: a.color }}
                      />
                    </div>
                  </div>
                </div>
                <p className="text-slate-500 text-xs leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>

          {/* Right: Donut + Details */}
          <div>
            {/* Donut chart (CSS) */}
            <div className="flex justify-center mb-10">
              <div className="relative w-56 h-56">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  {/* Track */}
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E8E4DC" strokeWidth="3.5" />
                  {/* 75% Property */}
                  <circle
                    cx="18" cy="18" r="15.9"
                    fill="none"
                    stroke="#C9A84C"
                    strokeWidth="3.5"
                    strokeDasharray="74.9 25.1"
                    strokeDashoffset="0"
                  />
                  {/* 15% Transaction - offset by 75 */}
                  <circle
                    cx="18" cy="18" r="15.9"
                    fill="none"
                    stroke="#4CAF8A"
                    strokeWidth="3.5"
                    strokeDasharray="14.9 85.1"
                    strokeDashoffset="-74.9"
                  />
                  {/* 10% Asset Setup - offset by 90 */}
                  <circle
                    cx="18" cy="18" r="15.9"
                    fill="none"
                    stroke="#6B8F6B"
                    strokeWidth="3.5"
                    strokeDasharray="9.9 90.1"
                    strokeDashoffset="-89.8"
                  />
                </svg>
                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-3xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>100%</p>
                  <p className="text-xs text-slate-400 mt-0.5">Deployed</p>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-col gap-3 mb-8">
              {allocations.map((a) => (
                <div key={a.label} className="flex items-center justify-between bg-white rounded-xl px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: a.color }} />
                    <span className="text-sm text-[#071A2E] font-medium">{a.label}</span>
                  </div>
                  <span className="font-bold text-[#071A2E] text-sm">{a.percentage}%</span>
                </div>
              ))}
            </div>

            <div className="bg-[#071A2E] rounded-2xl p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A84C]/20 text-[#C9A84C] flex-shrink-0">
                  <i className="ri-information-line text-sm leading-none" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold mb-1">Transparency Commitment</p>
                  <p className="text-slate-400 text-xs leading-relaxed">
                    Thorpe Dellow provides investors with full visibility into capital deployment. Detailed allocation reports are available within the Client Portal upon subscription.
                  </p>
                </div>
              </div>
              <a
                href="https://portal.thorpedellow.com"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 text-[#C9A84C] text-xs font-semibold hover:underline cursor-pointer"
              >
                <i className="ri-external-link-line leading-none" />
                Access Client Portal
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
